/**
 * Error States Components
 * 
 * Comprehensive error handling UI components for different error scenarios
 * throughout the application.
 * 
 * Features:
 * - Multiple error types (network, validation, not found, server)
 * - Contextual error messages and actions
 * - Retry mechanisms integration
 * - Accessibility compliant
 * - User-friendly error presentation
 */

import React from 'react';

// Error types
export type ErrorType = 'network' | 'validation' | 'notFound' | 'server' | 'permission' | 'timeout' | 'generic';

// Base error props
interface BaseErrorProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  showRetry?: boolean;
  retryText?: string;
  isRetrying?: boolean;
}

// Error icon component
const ErrorIcon: React.FC<{ type: ErrorType; className?: string }> = ({ type, className = '' }) => {
  const iconClasses = `w-12 h-12 ${className}`;
  
  switch (type) {
    case 'network':
      return (
        <svg className={`${iconClasses} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    case 'notFound':
      return (
        <svg className={`${iconClasses} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      );
    case 'permission':
      return (
        <svg className={`${iconClasses} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    case 'server':
      return (
        <svg className={`${iconClasses} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      );
    case 'timeout':
      return (
        <svg className={`${iconClasses} text-orange-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg className={`${iconClasses} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

// Generic error component
export const ErrorMessage: React.FC<BaseErrorProps & {
  type?: ErrorType;
}> = ({ 
  type = 'generic',
  title,
  message,
  onRetry,
  onDismiss,
  className = '',
  showRetry = true,
  retryText = 'Try Again',
  isRetrying = false
}) => {
  const defaultTitles = {
    network: 'Connection Error',
    validation: 'Invalid Input',
    notFound: 'Not Found',
    server: 'Server Error',
    permission: 'Access Denied',
    timeout: 'Request Timeout',
    generic: 'Something went wrong'
  };

  const defaultMessages = {
    network: 'Please check your internet connection and try again.',
    validation: 'Please check your input and try again.',
    notFound: 'The requested resource could not be found.',
    server: 'A server error occurred. Please try again later.',
    permission: 'You do not have permission to access this resource.',
    timeout: 'The request took too long to complete. Please try again.',
    generic: 'An unexpected error occurred. Please try again.'
  };

  return (
    <div className={`flex flex-col items-center text-center p-6 ${className}`} role="alert">
      <ErrorIcon type={type} className="mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {title || defaultTitles[type]}
      </h3>
      <p className="text-gray-600 mb-4 max-w-md">
        {message || defaultMessages[type]}
      </p>
      <div className="flex space-x-3">
        {showRetry && onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            aria-label={isRetrying ? 'Retrying...' : retryText}
          >
            {isRetrying && (
              <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            <span>{isRetrying ? 'Retrying...' : retryText}</span>
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

// Network error component
export const NetworkError: React.FC<BaseErrorProps> = (props) => (
  <ErrorMessage 
    {...props} 
    type="network"
    title={props.title || 'Connection Problem'}
    message={props.message || 'Unable to connect to our servers. Please check your internet connection.'}
  />
);

// Not found error component
export const NotFoundError: React.FC<BaseErrorProps & {
  resourceType?: string;
}> = ({ resourceType = 'page', ...props }) => (
  <ErrorMessage 
    {...props} 
    type="notFound"
    title={props.title || `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)} Not Found`}
    message={props.message || `The ${resourceType} you're looking for doesn't exist or has been moved.`}
    showRetry={false}
  />
);

// Server error component
export const ServerError: React.FC<BaseErrorProps> = (props) => (
  <ErrorMessage 
    {...props} 
    type="server"
    title={props.title || 'Server Error'}
    message={props.message || 'Our servers are experiencing issues. Please try again in a few moments.'}
  />
);

// Permission error component
export const PermissionError: React.FC<BaseErrorProps> = (props) => (
  <ErrorMessage 
    {...props} 
    type="permission"
    title={props.title || 'Access Denied'}
    message={props.message || 'You do not have permission to view this content.'}
    showRetry={false}
  />
);

// Timeout error component
export const TimeoutError: React.FC<BaseErrorProps> = (props) => (
  <ErrorMessage 
    {...props} 
    type="timeout"
    title={props.title || 'Request Timeout'}
    message={props.message || 'The request is taking longer than expected. Please try again.'}
  />
);

// Validation error component
export const ValidationError: React.FC<BaseErrorProps & {
  errors?: string[];
}> = ({ errors = [], ...props }) => (
  <div className={`p-4 border border-red-300 bg-red-50 rounded ${props.className || ''}`} role="alert">
    <div className="flex items-start">
      <ErrorIcon type="validation" className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
      <div className="flex-1">
        <h4 className="text-red-800 font-medium mb-1">
          {props.title || 'Validation Error'}
        </h4>
        {errors.length > 0 ? (
          <ul className="text-red-700 text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        ) : (
          <p className="text-red-700 text-sm">
            {props.message || 'Please correct the errors and try again.'}
          </p>
        )}
      </div>
      {props.onDismiss && (
        <button
          onClick={props.onDismiss}
          className="text-red-500 hover:text-red-700"
          aria-label="Dismiss error"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  </div>
);

// Inline error component
export const InlineError: React.FC<{
  message: string;
  className?: string;
}> = ({ message, className = '' }) => (
  <div className={`flex items-center text-red-600 text-sm ${className}`} role="alert">
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <span>{message}</span>
  </div>
);

// Error boundary fallback component
export const ErrorBoundaryFallback: React.FC<{
  error: Error;
  resetError: () => void;
  componentStack?: string;
}> = ({ error, resetError, componentStack }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-md w-full">
      <ErrorMessage
        type="server"
        title="Application Error"
        message="Something went wrong with this page. Our team has been notified."
        onRetry={resetError}
        retryText="Reload Page"
        className="bg-white rounded-lg shadow-lg"
      />
      {process.env.NODE_ENV === 'development' && (
        <details className="mt-4 p-4 bg-gray-100 rounded text-sm">
          <summary className="cursor-pointer font-medium">Error Details (Development)</summary>
          <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-700">
            {error.message}
            {componentStack && `\n\nComponent Stack:${componentStack}`}
          </pre>
        </details>
      )}
    </div>
  </div>
);

// Product loading error
export const ProductLoadingError: React.FC<BaseErrorProps> = (props) => (
  <div className="border rounded-lg p-6">
    <ErrorMessage
      {...props}
      type="network"
      title="Failed to Load Products"
      message="Unable to load products. Please check your connection and try again."
    />
  </div>
);

// Cart error component
export const CartError: React.FC<BaseErrorProps> = (props) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
    <ErrorMessage
      {...props}
      type="server"
      title="Cart Error"
      message="There was a problem with your cart. Please try again."
      className="bg-transparent p-0"
    />
  </div>
);

// Checkout error component
export const CheckoutError: React.FC<BaseErrorProps & {
  errors?: string[];
}> = ({ errors = [], ...props }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
    <div className="flex items-start">
      <ErrorIcon type="validation" className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
      <div className="flex-1">
        <h4 className="text-red-800 font-medium mb-2">
          {props.title || 'Checkout Error'}
        </h4>
        {errors.length > 0 ? (
          <ul className="text-red-700 text-sm space-y-1 mb-3">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        ) : (
          <p className="text-red-700 text-sm mb-3">
            {props.message || 'Please review your order and try again.'}
          </p>
        )}
        {props.onRetry && (
          <button
            onClick={props.onRetry}
            disabled={props.isRetrying}
            className="text-red-800 underline hover:no-underline text-sm font-medium"
          >
            {props.isRetrying ? 'Retrying...' : 'Try Again'}
          </button>
        )}
      </div>
    </div>
  </div>
);

// Error toast notification
export const ErrorToast: React.FC<{
  message: string;
  isVisible: boolean;
  onDismiss: () => void;
  autoHide?: boolean;
  duration?: number;
}> = ({ message, isVisible, onDismiss, autoHide = true, duration = 5000 }) => {
  React.useEffect(() => {
    if (isVisible && autoHide) {
      const timer = setTimeout(onDismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHide, duration, onDismiss]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-red-600 text-white p-4 rounded-lg shadow-lg flex items-center justify-between">
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">{message}</span>
        </div>
        <button
          onClick={onDismiss}
          className="ml-4 text-white hover:text-gray-200"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
